import React, { useMemo } from 'react'
import { AppProps } from 'next/app'
import { NextRouter, useRouter as useNextRouter, useRouter } from 'next/router'

import { RouterContext } from 'next/dist/shared/lib/router-context.shared-runtime'

import { MarketContext } from '@/utils/multi-markets/context'
import { Locale, Url } from '@/utils/i18n/types'
import {
  dynamicRouteFromQuery,
  translatePathnameLinks
} from '@/utils/multi-markets/common'

export function toTranslatedLink(
  url: Url,
  as: Url | undefined,
  router: NextRouter,
  locale: Locale,
  defaultLocale: Locale
) {
  const pathname =
    (typeof url === 'object' ? url.pathname : url) ?? router.pathname

  const shouldTranslateLink = as === undefined && pathname.startsWith('/')

  if (shouldTranslateLink) {
    const translatedPathname = translatePathnameLinks(
      pathname,
      locale,
      defaultLocale
    )

    let query: any = {}

    if (typeof url === 'object' && url.query) {
      query = typeof url.query === 'string' ? {} : { ...url.query }
    } else if (typeof url === 'string' && url.includes('?')) {
      const [, queryString] = url.split('?')
      if (queryString) {
        queryString.split('&').forEach(param => {
          if (param) {
            const [key, value] = param.split('=')
            query[key] = value
          }
        })
      }
    }

    return {
      href: {
        pathname: translatedPathname,
        query: query
      },
      as: undefined,
      options: { locale: false as const }
    }
  } else {
    return { href: url, as }
  }
}

function enhanceNextRouter(
  router: NextRouter,
  pageProps: AppProps['pageProps']
): NextRouter {
  //@ts-ignore
  const market: MarketContext = pageProps?.market

  const locale =
    router.locale === 'default' || !router.locale
      ? market.defaultLocale
      : (router.locale as Locale)

  const push: NextRouter['push'] = (url, as, options) => {
    const link = toTranslatedLink(
      url,
      as,
      router,
      typeof options?.locale === 'string' ? (options.locale as Locale) : locale,
      market.defaultLocale
    )

    return router.push(link.href, link.as, { ...options, ...link.options })
  }

  const replace: NextRouter['replace'] = (url, as, options) => {
    const link = toTranslatedLink(url, as, router, locale, market.defaultLocale)

    return router.replace(link.href, link.as, { ...options, ...link.options })
  }

  return {
    ...router,
    locale,
    push,
    replace
  }
}

type Props = React.PropsWithChildren<Pick<AppProps, 'pageProps'>>

function EnhancedRouterProvider(props: Props) {
  const { pageProps, children } = props
  const nextRouter = useNextRouter()

  const enhancedRouter = useMemo(
    () => enhanceNextRouter(nextRouter, pageProps),
    [nextRouter, pageProps]
  )

  return (
    <RouterContext.Provider value={enhancedRouter}>
      {children}
    </RouterContext.Provider>
  )
}

export default EnhancedRouterProvider
